import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  HardHat,
  SignOut,
  Lightning,
  Trophy,
  Eye,
  MapPin,
  CalendarBlank,
  Package,
  Clock,
  PlusCircle,
  Gear,
  User,
  Phone,
  Buildings,
  Image as ImageIcon,
  FileText,
  Trash,
  MagnifyingGlass,
  X,
  Minus,
  Plus,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import api, {
  uploadSupplierProductImage,
  PRODUCT_IMAGE_MAX_KB,
  PRODUCT_IMAGE_MAX_BYTES,
  publicAssetUrl,
} from "@/lib/api";
import { parseProductsSupplied, filterProductsSuppliedToCatalog } from "@/lib/supplierProducts";
import { parseProductSizes, formatLineSizeDisplay } from "@/lib/productSizes";
import { FIXED_PIPELINE_STAGES, mergePipelineStages, getStageMeta, stageChoicesForAssignment } from "@/lib/supplierPipeline";
import CityGoogleAutocomplete from "@/components/CityGoogleAutocomplete";

const ADD_LEAD_CUSTOM_PRODUCT = "__custom__";
const addLeadFieldClassName = "mt-1 h-9 rounded-sm border-neutral-300 text-sm";
const addLeadNestedFieldClassName = "h-9 rounded-sm border-neutral-300 text-sm";
const addLeadCityClassName = "add-lead-city-autocomplete mt-1 w-full";

function leadRequirementLabel(requirementType) {
  return String(requirementType || "rent").toLowerCase() === "buy" ? "Buy" : "Rent";
}

function leadDetailValue(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function initialSupplierLeadItem() {
  return {
    line_pick: ADD_LEAD_CUSTOM_PRODUCT,
    product_id: null,
    product_name: "",
    size: "",
    spec_type: "quantity",
    quantity: "1",
    area_value: "",
    area_unit: "sqft",
  };
}

export default function SupplierDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [badges, setBadges] = useState({});
  const [activeStage, setActiveStage] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("leads");
  const supplierTabOptions = useMemo(
    () => [
      { value: "leads", label: "Lead pipeline" },
      { value: "catalog", label: "Catalog requests" },
      { value: "profile", label: "Profile" },
    ],
    []
  );
  const [productRequests, setProductRequests] = useState([]);
  const [categoryNames, setCategoryNames] = useState([]);
  const [requestForm, setRequestForm] = useState({
    name: "",
    category: "General",
    description: "",
    image_url: "",
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestImageFile, setRequestImageFile] = useState(null);
  const requestImagePreview = useMemo(
    () => (requestImageFile ? URL.createObjectURL(requestImageFile) : ""),
    [requestImageFile]
  );
  useEffect(() => {
    return () => {
      if (requestImagePreview) URL.revokeObjectURL(requestImagePreview);
    };
  }, [requestImagePreview]);
  const [profileName, setProfileName] = useState("");
  const [profileCompany, setProfileCompany] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileSupplierType, setProfileSupplierType] = useState("hybrid");
  const [profileCities, setProfileCities] = useState([]);
  const [profileCitySearch, setProfileCitySearch] = useState("");
  /** @type {{ product_id: string, quantity: string, rate_per_day: string, weight: string }[]} */
  const [profileProductLines, setProfileProductLines] = useState([]);
  const [catalogForProfile, setCatalogForProfile] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSubTab, setProfileSubTab] = useState("personal");
  const [productToAdd, setProductToAdd] = useState("");
  const [profileLogoUrl, setProfileLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileInputRef = useRef(null);
  const [quotationSupplierName, setQuotationSupplierName] = useState("");
  const [quotationSupplierAddress, setQuotationSupplierAddress] = useState("");
  const [quotationSupplierGstin, setQuotationSupplierGstin] = useState("");
  const [quotationSecurityAmount, setQuotationSecurityAmount] = useState("");
  const [quotationTerms, setQuotationTerms] = useState("");
  const [quotationWatermarkUrl, setQuotationWatermarkUrl] = useState("");
  const [quotationWatermarkUploading, setQuotationWatermarkUploading] = useState(false);
  const quotationWatermarkInputRef = useRef(null);
  const [showAddLeadDialog, setShowAddLeadDialog] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState("");
  /** Products this supplier has on profile (for Add Lead dropdown). */
  const [leadFormCatalogProducts, setLeadFormCatalogProducts] = useState([]);
  const [leadFormCatalogLoading, setLeadFormCatalogLoading] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    buyer_name: "",
    buyer_company: "",
    buyer_phone: "",
    city: "",
    requirement_type: "rent",
    start_date: new Date().toISOString().slice(0, 10),
    duration: "",
    items: [initialSupplierLeadItem()],
  });
  const [leadSearchInput, setLeadSearchInput] = useState("");
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState("");
  const [quotationsModalAssignment, setQuotationsModalAssignment] = useState(null);
  const [quotationsList, setQuotationsList] = useState([]);
  const [quotationsListLoading, setQuotationsListLoading] = useState(false);
  const [customPipelineStages, setCustomPipelineStages] = useState([]);
  const [showPipelineSetup, setShowPipelineSetup] = useState(false);
  const [newPipelineStageLabel, setNewPipelineStageLabel] = useState("");
  const [pipelineSaving, setPipelineSaving] = useState(false);

  const pipelineStages = useMemo(
    () => mergePipelineStages(FIXED_PIPELINE_STAGES, customPipelineStages),
    [customPipelineStages]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedLeadSearch(leadSearchInput.trim().toLowerCase()), 400);
    return () => clearTimeout(t);
  }, [leadSearchInput]);

  useEffect(() => {
    setSelectedLead((prev) => {
      if (!prev?.id) return prev;
      const fresh = leads.find((l) => l.id === prev.id);
      if (!fresh) return prev;
      if (
        prev.status === fresh.status &&
        (prev.quotation_count || 0) === (fresh.quotation_count || 0) &&
        prev.opened_at === fresh.opened_at &&
        prev.contacted_at === fresh.contacted_at
      ) {
        return prev;
      }
      return {
        ...prev,
        status: fresh.status,
        quotation_count: fresh.quotation_count,
        opened_at: fresh.opened_at,
        contacted_at: fresh.contacted_at,
        notes: prev.notes,
      };
    });
  }, [leads]);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, badgesRes, pipelineRes] = await Promise.all([
        api.get("/supplier/leads?limit=500"),
        api.get("/supplier/badges"),
        api.get("/supplier/pipeline"),
      ]);
      setLeads(leadsRes.data.items || leadsRes.data);
      setBadges(badgesRes.data);
      setCustomPipelineStages(pipelineRes.data.custom || []);
    } catch (err) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, []);

  const fetchProductRequests = useCallback(async () => {
    try {
      const [prRes, catRes] = await Promise.all([
        api.get("/supplier/product-requests"),
        api.get("/public/product-category-names"),
      ]);
      setProductRequests(prRes.data.items || []);
      const names = catRes.data || [];
      setCategoryNames(names.length ? names : ["General"]);
    } catch (err) {
      toast.error("Failed to load catalog requests");
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const assignmentFromUrl = searchParams.get("assignment");
  useEffect(() => {
    if (!assignmentFromUrl || loading) return;

    const openModal = (a) => {
      setSelectedLead(a);
      setNotes(a.notes || "");
      setMainTab("leads");
      navigate("/supplier", { replace: true });
    };

    // Same render as loading→false batches setLeads + setLoading, so `leads` is current here.
    const fromList = leads.find((x) => x.id === assignmentFromUrl);
    if (fromList) {
      openModal(fromList);
      return;
    }

    const ac = new AbortController();
    (async () => {
      try {
        const r = await api.get(`/supplier/assignments/${assignmentFromUrl}`, {
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        const data = r.data;
        openModal(data);
        setLeads((prev) => (prev.some((x) => x.id === data.id) ? prev : [data, ...prev]));
      } catch (e) {
        if (ac.signal.aborted) return;
        const msg = e?.code === "ERR_CANCELED" || e?.name === "CanceledError";
        if (msg) return;
        toast.error("That lead link is invalid or no longer available.");
        navigate("/supplier", { replace: true });
      }
    })();

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-running when `leads` updates (would abort fetch)
  }, [assignmentFromUrl, loading, navigate]);

  useEffect(() => {
    if (mainTab === "catalog") fetchProductRequests();
  }, [mainTab, fetchProductRequests]);

  const fetchProfileTab = useCallback(async () => {
    setProfileLoaded(false);
    try {
      const [profRes, prodRes] = await Promise.all([
        api.get("/supplier/profile"),
        api.get("/products?limit=200"),
      ]);
      const p = profRes.data;
      setProfileName(p.name || "");
      setProfileCompany(p.company_name || "");
      setProfilePhone(p.phone || "");
      setProfileEmail(p.email || "");
      setProfileSupplierType(
        ["rental", "sale", "hybrid"].includes(p.supplier_type) ? p.supplier_type : "hybrid"
      );
      setProfileCities(p.cities_served || []);
      setProfileCitySearch("");
      setProfileLogoUrl(p.logo_url || "");
      setQuotationSupplierName(p.quotation_supplier_name || p.name || "");
      setQuotationSupplierAddress(p.quotation_supplier_address || p.company_name || "");
      setQuotationSupplierGstin(p.quotation_supplier_gstin || "");
      setQuotationSecurityAmount(
        p.quotation_security_amount != null ? String(p.quotation_security_amount) : ""
      );
      setQuotationTerms(p.quotation_terms || "");
      setQuotationWatermarkUrl(p.quotation_watermark_url || "");
      const items = prodRes.data.items || prodRes.data || [];
      const catalog = Array.isArray(items) ? items : [];
      setCatalogForProfile(catalog);
      const parsed = filterProductsSuppliedToCatalog(
        parseProductsSupplied(p.products_supplied),
        catalog
      );
      setProfileProductLines(
        parsed.map((row) => {
          const q = row.quantity != null && row.quantity >= 1 ? row.quantity : 1;
          return {
            product_id: row.product_id,
            quantity: String(q),
            rate_per_day: row.rate_per_day != null ? String(row.rate_per_day) : "",
            weight: row.weight != null ? String(row.weight) : "",
          };
        })
      );
    } catch (err) {
      toast.error("Failed to load profile");
    }
    setProfileLoaded(true);
  }, []);

  useEffect(() => {
    if (mainTab === "profile") fetchProfileTab();
  }, [mainTab, fetchProfileTab]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      if (!profileName.trim()) {
        toast.error("Your name is required");
        setSavingProfile(false);
        return;
      }
      if (!profilePhone.trim()) {
        toast.error("Phone number is required");
        setSavingProfile(false);
        return;
      }
      const invalidQty = profileProductLines.some((l) => {
        const n = parseInt(l.quantity, 10);
        return l.quantity === "" || !Number.isFinite(n) || n < 1;
      });
      if (invalidQty) {
        toast.error("Each selected product needs a quantity of at least 1");
        setSavingProfile(false);
        return;
      }
      const invalidRate = profileProductLines.some((l) => {
        if (String(l.rate_per_day).trim() === "") return false;
        const n = Number(l.rate_per_day);
        return !Number.isFinite(n) || n < 0;
      });
      if (invalidRate) {
        toast.error("Rate/day must be a valid non-negative number");
        setSavingProfile(false);
        return;
      }
      const invalidWeight = profileProductLines.some((l) => {
        if (String(l.weight).trim() === "") return false;
        const n = Number(l.weight);
        return !Number.isFinite(n) || n < 0;
      });
      if (invalidWeight) {
        toast.error("Weight must be a valid non-negative number");
        setSavingProfile(false);
        return;
      }
      if (String(quotationSecurityAmount).trim() !== "") {
        const n = Number(quotationSecurityAmount);
        if (!Number.isFinite(n) || n < 0) {
          toast.error("Quotation security amount must be a non-negative number");
          setSavingProfile(false);
          return;
        }
      }
      await api.put("/supplier/profile", {
        name: profileName.trim(),
        company_name: profileCompany.trim(),
        phone: profilePhone.trim(),
        supplier_type: profileSupplierType,
        quotation_supplier_name: quotationSupplierName.trim(),
        quotation_supplier_address: quotationSupplierAddress.trim(),
        quotation_supplier_gstin: quotationSupplierGstin.trim(),
        quotation_security_amount:
          String(quotationSecurityAmount).trim() === ""
            ? null
            : Number(quotationSecurityAmount),
        quotation_terms: quotationTerms,
        quotation_watermark_url: quotationWatermarkUrl || "",
        cities_served: profileCities,
        products_supplied: profileProductLines.map((l) => ({
          product_id: l.product_id,
          quantity: parseInt(l.quantity, 10),
          rate_per_day: String(l.rate_per_day).trim() === "" ? null : Number(l.rate_per_day),
          weight: String(l.weight).trim() === "" ? null : Number(l.weight),
        })),
      });
      toast.success("Profile saved");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    }
    setSavingProfile(false);
  };

  const onLogoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
      toast.error(`Image must be at most ${PRODUCT_IMAGE_MAX_KB} KB`);
      return;
    }
    setLogoUploading(true);
    try {
      const path = await uploadSupplierProductImage(file);
      await api.put("/supplier/profile", { logo_url: path });
      setProfileLogoUrl(path);
      toast.success("Logo saved");
      await refreshUser();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        (typeof err === "string" ? err : "Upload failed");
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    }
    setLogoUploading(false);
  };

  const removeProfileLogo = async () => {
    setLogoUploading(true);
    try {
      await api.put("/supplier/profile", { logo_url: "" });
      setProfileLogoUrl("");
      toast.success("Logo removed");
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove logo");
    }
    setLogoUploading(false);
  };

  const onQuotationWatermarkSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
      toast.error(`Image must be at most ${PRODUCT_IMAGE_MAX_KB} KB`);
      return;
    }
    setQuotationWatermarkUploading(true);
    try {
      const path = await uploadSupplierProductImage(file);
      setQuotationWatermarkUrl(path);
      toast.success("Quotation watermark uploaded");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        (typeof err === "string" ? err : "Upload failed");
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    }
    setQuotationWatermarkUploading(false);
  };

  const toggleProfileCity = (city) => {
    setProfileCities((prev) =>
      prev.includes(city) ? prev.filter((c) => c !== city) : [...prev, city]
    );
  };

  const normalizeProfileCity = (value) => String(value || "").trim().replace(/\s+/g, " ");

  const addProfileCityFromSearch = useCallback((city) => {
    const normalized = normalizeProfileCity(city);
    if (!normalized) return;
    setProfileCities((prev) => {
      if (prev.some((c) => c.toLowerCase() === normalized.toLowerCase())) return prev;
      return [...prev, normalized];
    });
    setProfileCitySearch("");
  }, []);

  const toggleProfileProduct = (productId) => {
    setProfileProductLines((prev) => {
      const i = prev.findIndex((l) => l.product_id === productId);
      if (i >= 0) return prev.filter((_, idx) => idx !== i);
      return [...prev, { product_id: productId, quantity: "1", rate_per_day: "", weight: "" }];
    });
  };

  const setProfileLineQty = (productId, raw) => {
    const digits = String(raw).replace(/\D/g, "");
    setProfileProductLines((prev) =>
      prev.map((l) => (l.product_id === productId ? { ...l, quantity: digits } : l))
    );
  };

  const setProfileLineRate = (productId, raw) => {
    const cleaned = String(raw).replace(/[^0-9.]/g, "");
    setProfileProductLines((prev) =>
      prev.map((l) => (l.product_id === productId ? { ...l, rate_per_day: cleaned } : l))
    );
  };

  const setProfileLineWeight = (productId, raw) => {
    const cleaned = String(raw).replace(/[^0-9.]/g, "");
    setProfileProductLines((prev) =>
      prev.map((l) => (l.product_id === productId ? { ...l, weight: cleaned } : l))
    );
  };

  const addProfileProduct = () => {
    const pid = (productToAdd || "").trim();
    if (!pid) return;
    setProfileProductLines((prev) => {
      if (prev.some((x) => x.product_id === pid)) return prev;
      return [...prev, { product_id: pid, quantity: "1", rate_per_day: "", weight: "" }];
    });
    setProductToAdd("");
  };

  const removeProfileProduct = (productId) => {
    setProfileProductLines((prev) => prev.filter((x) => x.product_id !== productId));
  };

  const submitProductRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.name.trim()) {
      toast.error("Enter a product name");
      return;
    }
    setSubmittingRequest(true);
    try {
      let imageUrl = requestForm.image_url.trim();
      if (requestImageFile) {
        imageUrl = await uploadSupplierProductImage(requestImageFile);
      }
      await api.post("/supplier/product-requests", {
        name: requestForm.name.trim(),
        category: requestForm.category || "General",
        description: requestForm.description.trim(),
        image_url: imageUrl,
      });
      toast.success("Request submitted. An admin will review it.");
      setRequestImageFile(null);
      setRequestForm({ name: "", category: categoryNames[0] || "General", description: "", image_url: "" });
      fetchProductRequests();
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Failed to submit request");
    }
    setSubmittingRequest(false);
  };

  const updateStage = async (assignmentId, newStatus) => {
    const stageLabel = getStageMeta(pipelineStages, newStatus).label;
    try {
      await api.put(`/supplier/assignments/${assignmentId}/stage`, { status: newStatus });
      setLeads((prev) =>
        prev.map((assignment) =>
          assignment.id === assignmentId ? { ...assignment, status: newStatus } : assignment
        )
      );
      toast.success(`Stage updated to ${stageLabel}`);
      if (selectedLead?.id === assignmentId) {
        setSelectedLead((prev) => (prev ? { ...prev, status: newStatus } : prev));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update stage");
      fetchData();
    }
  };

  const addCustomPipelineStage = async (e) => {
    e?.preventDefault?.();
    const label = newPipelineStageLabel.trim();
    if (!label) {
      toast.error("Enter a stage name");
      return;
    }
    setPipelineSaving(true);
    try {
      const res = await api.post("/supplier/pipeline/stages", { label });
      setCustomPipelineStages((prev) => [...prev, res.data]);
      setNewPipelineStageLabel("");
      toast.success(`Stage "${res.data.label}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stage");
    }
    setPipelineSaving(false);
  };

  const deleteCustomPipelineStage = async (stageKey) => {
    setPipelineSaving(true);
    try {
      await api.delete(`/supplier/pipeline/stages/${encodeURIComponent(stageKey)}`);
      setCustomPipelineStages((prev) => prev.filter((s) => s.key !== stageKey));
      if (activeStage === stageKey) setActiveStage("all");
      toast.success("Stage removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove stage");
    }
    setPipelineSaving(false);
  };

  const saveNotes = async (assignmentId) => {
    try {
      await api.put(`/supplier/assignments/${assignmentId}/notes`, { notes });
      toast.success("Notes saved");
      fetchData();
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  const goToQuotationBuilder = (assignment, quotationId = null) => {
    if (!assignment?.id) return;
    const q = quotationId ? `?q=${encodeURIComponent(quotationId)}` : "";
    navigate(`/supplier/quotation/${assignment.id}${q}`, { state: { assignment } });
  };

  const openQuotationsModal = async (assignment) => {
    setQuotationsModalAssignment(assignment);
    setQuotationsListLoading(true);
    setQuotationsList([]);
    try {
      const r = await api.get(`/supplier/assignments/${assignment.id}/quotations`);
      setQuotationsList(r.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load quotations");
      setQuotationsList([]);
    }
    setQuotationsListLoading(false);
  };

  const loadLeadFormCatalog = useCallback(async () => {
    setLeadFormCatalogLoading(true);
    try {
      const [profRes, prodRes] = await Promise.all([
        api.get("/supplier/profile"),
        api.get("/products?limit=500"),
      ]);
      const parsed = parseProductsSupplied(profRes.data?.products_supplied);
      const idSet = new Set(parsed.map((p) => p.product_id).filter(Boolean));
      const raw = prodRes.data?.items ?? prodRes.data;
      const all = Array.isArray(raw) ? raw : [];
      const supplied = all.filter((p) => p?.id && idSet.has(p.id));
      supplied.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setLeadFormCatalogProducts(supplied);
    } catch {
      setLeadFormCatalogProducts([]);
      toast.error("Could not load your catalog products");
    }
    setLeadFormCatalogLoading(false);
  }, []);

  const openAddLeadDialog = () => {
    setNewLeadForm({
      buyer_name: "",
      buyer_company: "",
      buyer_phone: "",
      city: "",
      requirement_type: "rent",
      start_date: new Date().toISOString().slice(0, 10),
      duration: "",
      items: [initialSupplierLeadItem()],
    });
    setShowAddLeadDialog(true);
    loadLeadFormCatalog();
  };

  const updateNewLeadItem = (idx, key, value) => {
    setNewLeadForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));
  };

  const patchNewLeadItem = (idx, patch) => {
    setNewLeadForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const onAddLeadProductPick = (idx, value) => {
    if (value === ADD_LEAD_CUSTOM_PRODUCT) {
      patchNewLeadItem(idx, {
        line_pick: ADD_LEAD_CUSTOM_PRODUCT,
        product_id: null,
        product_name: "",
        size: "",
      });
      return;
    }
    const p = leadFormCatalogProducts.find((x) => x.id === value);
    const sizeOpts = parseProductSizes(p?.sizes);
    patchNewLeadItem(idx, {
      line_pick: value,
      product_id: value,
      product_name: (p?.name || "").trim(),
      size: sizeOpts.length === 1 ? sizeOpts[0] : "",
    });
  };

  const onAddLeadSpecTypeChange = (idx, specType) => {
    const st = specType === "area" ? "area" : "quantity";
    patchNewLeadItem(idx, {
      spec_type: st,
      ...(st === "area"
        ? { quantity: "1" }
        : { area_value: "", area_unit: "sqft" }),
    });
  };

  const addNewLeadItem = () => {
    setNewLeadForm((prev) => ({ ...prev, items: [...prev.items, initialSupplierLeadItem()] }));
  };

  const removeNewLeadItem = (idx) => {
    setNewLeadForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items,
    }));
  };

  const submitSupplierLead = async (e) => {
    e.preventDefault();
    if (!newLeadForm.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!newLeadForm.start_date) {
      toast.error("Start date is required");
      return;
    }
    if (newLeadForm.requirement_type === "rent" && !newLeadForm.duration.trim()) {
      toast.error("Duration is required");
      return;
    }
    if (!newLeadForm.buyer_phone.trim() || newLeadForm.buyer_phone.trim().length < 10) {
      toast.error("Enter a valid buyer phone number");
      return;
    }
    const normalizedItems = [];
    for (const it of newLeadForm.items) {
      const name = String(it.product_name || "").trim();
      if (!name) {
        toast.error("Each line needs a product (pick from your catalog or enter a custom name)");
        return;
      }
      const st = it.spec_type === "area" ? "area" : "quantity";
      const pid = it.product_id && String(it.product_id).trim() ? String(it.product_id).trim() : null;
      const catalogP = pid ? leadFormCatalogProducts.find((x) => x.id === pid) : null;
      const sizeOpts = catalogP ? parseProductSizes(catalogP.sizes) : [];
      const sizeVal = String(it.size || "").trim();
      if (sizeOpts.length > 0 && !sizeVal) {
        toast.error(`Select a size for “${name}”`);
        return;
      }
      const withSize = (row) => (sizeVal ? { ...row, size: sizeVal } : row);
      if (st === "area") {
        const av = parseFloat(String(it.area_value ?? "").replace(/,/g, "").trim());
        if (!Number.isFinite(av) || av <= 0) {
          toast.error(`Enter a valid area greater than 0 for “${name}”`);
          return;
        }
        const unit = it.area_unit === "sqm" ? "sqm" : "sqft";
        normalizedItems.push(
          withSize({
            product_name: name,
            product_id: pid,
            spec_type: "area",
            quantity: null,
            area_value: av,
            area_unit: unit,
          })
        );
      } else {
        const qty = parseInt(String(it.quantity || ""), 10);
        if (!Number.isFinite(qty) || qty < 1) {
          toast.error(`Enter a valid quantity (at least 1) for “${name}”`);
          return;
        }
        normalizedItems.push(
          withSize({
            product_name: name,
            product_id: pid,
            spec_type: "quantity",
            quantity: qty,
            area_value: null,
            area_unit: null,
          })
        );
      }
    }
    setCreatingLead(true);
    try {
      await api.post("/supplier/leads", {
        buyer_name: newLeadForm.buyer_name.trim(),
        buyer_company: newLeadForm.buyer_company.trim(),
        buyer_phone: newLeadForm.buyer_phone.trim(),
        city: newLeadForm.city.trim(),
        requirement_type: newLeadForm.requirement_type === "buy" ? "buy" : "rent",
        start_date: newLeadForm.start_date,
        duration:
          newLeadForm.requirement_type === "rent" ? newLeadForm.duration.trim() : "N/A",
        items: normalizedItems,
      });
      toast.success("Lead added to your pipeline");
      setShowAddLeadDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lead");
    }
    setCreatingLead(false);
  };

  const deleteCustomLead = async (assignment) => {
    const leadId = assignment?.lead?.id || assignment?.lead_id;
    if (!leadId) return;
    const ok = window.confirm("Delete this custom lead? This action cannot be undone.");
    if (!ok) return;
    setDeletingLeadId(leadId);
    try {
      await api.delete(`/supplier/leads/${leadId}`);
      if (selectedLead?.lead?.id === leadId) setSelectedLead(null);
      toast.success("Custom lead deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete lead");
    }
    setDeletingLeadId("");
  };

  const stageFiltered = useMemo(
    () => (activeStage === "all" ? leads : leads.filter((l) => l.status === activeStage)),
    [leads, activeStage]
  );

  const filtered = useMemo(() => {
    if (!debouncedLeadSearch) return stageFiltered;
    const q = debouncedLeadSearch;
    return stageFiltered.filter((assignment) => {
      const lead = assignment.lead || {};
      const stageLabel = (getStageMeta(pipelineStages, assignment.status).label || "").toLowerCase();
      const items = Array.isArray(lead.items) ? lead.items : [];
      const haystack = [
        assignment.id,
        lead.buyer_phone,
        lead.buyer_name,
        lead.buyer_company,
        lead.city,
        leadRequirementLabel(lead.requirement_type),
        stageLabel,
        ...items.map((it) => it.product_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [stageFiltered, debouncedLeadSearch, pipelineStages]);

  const stageCounts = useMemo(
    () =>
      pipelineStages.reduce((acc, s) => {
        acc[s.key] = leads.filter((l) => l.status === s.key).length;
        return acc;
      }, {}),
    [pipelineStages, leads]
  );

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200" data-testid="supplier-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardHat size={28} weight="bold" className="text-orange-600" />
            <span className="text-xl font-bold tracking-tight">SCAFFOINDIA</span>
            <Badge className="rounded-sm bg-orange-100 text-orange-800 border border-orange-300 text-xs ml-2">SUPPLIER</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-600 hidden sm:block">{user?.name}</span>
            <Button data-testid="supplier-logout-btn" variant="outline" size="sm" onClick={handleLogout} className="rounded-sm border-neutral-300">
              <SignOut size={16} weight="bold" className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Badges & Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-sm border-neutral-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black">{leads.length}</p>
              <p className="text-xs text-neutral-500 font-mono uppercase tracking-wider mt-1">Total Leads</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-neutral-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-green-600">{badges.total_won || 0}</p>
              <p className="text-xs text-neutral-500 font-mono uppercase tracking-wider mt-1">Won</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-neutral-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-orange-600">{badges.performance_score || 0}%</p>
              <p className="text-xs text-neutral-500 font-mono uppercase tracking-wider mt-1">Score</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-neutral-200">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              {badges.first_responder_count > 0 && (
                <Badge className="badge-first-responder rounded-sm text-xs mb-1" data-testid="first-responder-badge">
                  <Lightning size={12} weight="fill" className="mr-1" /> First Responder x{badges.first_responder_count}
                </Badge>
              )}
              {badges.is_top_supplier && (
                <Badge className="badge-top-supplier rounded-sm text-xs" data-testid="top-supplier-badge">
                  <Trophy size={12} weight="fill" className="mr-1" /> Top Supplier
                </Badge>
              )}
              {!badges.first_responder_count && !badges.is_top_supplier && (
                <p className="text-xs text-neutral-400">No badges yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab} className="mb-6">
          <div className="sm:hidden mb-4">
            <Select
              value={supplierTabOptions.some((t) => t.value === mainTab) ? mainTab : "leads"}
              onValueChange={setMainTab}
            >
              <SelectTrigger className="rounded-sm border-neutral-300 bg-white" data-testid="supplier-tab-select">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent side="bottom" align="start" sideOffset={6} className="max-h-[320px]">
                {supplierTabOptions.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <TabsList className="hidden sm:flex bg-white border border-neutral-200 rounded-sm h-11 mb-6 p-1">
            <TabsTrigger value="leads" data-testid="supplier-tab-leads" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              Lead pipeline
            </TabsTrigger>
            <TabsTrigger value="catalog" data-testid="supplier-tab-catalog" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <PlusCircle size={16} className="mr-1 hidden sm:inline" /> Catalog requests
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="supplier-tab-profile" className="rounded-sm text-xs sm:text-sm font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">
              <User size={16} className="mr-1 hidden sm:inline" /> Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-0">
            <div className="mb-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-2xl font-bold tracking-tight">Lead Pipeline</h2>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPipelineSetup(true)}
                    className="rounded-sm border-neutral-300"
                    data-testid="supplier-setup-pipeline-btn"
                  >
                    <Gear size={16} className="mr-1" /> Setup Pipeline
                  </Button>
                  <Button
                    type="button"
                    onClick={openAddLeadDialog}
                    className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="supplier-add-lead-btn"
                  >
                    <PlusCircle size={16} className="mr-1" /> Add New Lead
                  </Button>
                </div>
              </div>
              <div className="mb-6">
                <div className="sm:hidden">
                  <Select value={activeStage} onValueChange={setActiveStage}>
                    <SelectTrigger className="rounded-sm border-neutral-300 bg-white" data-testid="supplier-stage-select">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent side="bottom" align="start" sideOffset={6} className="max-h-[320px]">
                      <SelectItem value="all">All ({leads.length})</SelectItem>
                      {pipelineStages.map((s) => (
                        <SelectItem key={s.key} value={s.key}>
                          {s.label} ({stageCounts[s.key] || 0})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="hidden sm:flex flex-wrap gap-2">
                  <Button
                    data-testid="filter-all-btn"
                    variant={activeStage === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveStage("all")}
                    className={`rounded-sm ${activeStage === "all" ? "bg-neutral-900 text-white" : "border-neutral-300"}`}
                  >
                    All ({leads.length})
                  </Button>
                  {pipelineStages.map((s) => (
                    <Button
                      key={s.key}
                      data-testid={`filter-${s.key}-btn`}
                      variant={activeStage === s.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveStage(s.key)}
                      className={`rounded-sm ${activeStage === s.key ? "bg-neutral-900 text-white" : "border-neutral-300"}`}
                    >
                      {s.label} ({stageCounts[s.key] || 0})
                    </Button>
                  ))}
                </div>
              </div>

              <div className="relative mb-4 max-w-md">
                <MagnifyingGlass
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                  aria-hidden
                />
                <Input
                  data-testid="supplier-lead-search"
                  className="rounded-sm border-neutral-300 pl-9 pr-9"
                  placeholder="Search leads (city, phone, buyer, products…)"
                  value={leadSearchInput}
                  onChange={(e) => setLeadSearchInput(e.target.value)}
                  aria-label="Search leads"
                />
                {leadSearchInput ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-neutral-500 hover:bg-neutral-100"
                    onClick={() => setLeadSearchInput("")}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                ) : null}
              </div>

              {loading ? (
                <div className="text-center py-12 text-neutral-500">Loading leads...</div>
              ) : stageFiltered.length === 0 ? (
                <div className="text-center py-12 text-neutral-500">No leads in this stage</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-neutral-500">No leads match your search</div>
              ) : (
                <div className="space-y-3">
                  {filtered.map(assignment => (
                    <Card key={assignment.id} className={`rounded-sm border-neutral-200 card-hover stage-${assignment.status}`} data-testid={`lead-card-${assignment.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`rounded-sm text-xs border ${getStageMeta(pipelineStages, assignment.status).color}`}>
                                {getStageMeta(pipelineStages, assignment.status).label}
                              </Badge>
                              {assignment.is_first_responder && (
                                <Badge className="badge-first-responder rounded-sm text-xs">
                                  <Lightning size={10} weight="fill" className="mr-0.5" /> First
                                </Badge>
                              )}
                              <span className="text-xs font-mono text-neutral-400">{assignment.id.slice(0, 8)}</span>
                            </div>
                            {assignment.lead && (
                              <div className="space-y-1.5">
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600 items-center">
                                  <Badge
                                    variant="outline"
                                    className={`rounded-sm text-[10px] font-semibold uppercase ${
                                      leadRequirementLabel(assignment.lead.requirement_type) === "Buy"
                                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                        : "border-sky-300 bg-sky-50 text-sky-800"
                                    }`}
                                  >
                                    {leadRequirementLabel(assignment.lead.requirement_type)}
                                  </Badge>
                                  <span className="flex items-center gap-1"><MapPin size={14} /> {assignment.lead.city}</span>
                                  <span className="flex items-center gap-1"><Package size={14} /> {assignment.lead.items?.length || 0} items</span>
                                  <span className="flex items-center gap-1"><CalendarBlank size={14} /> {assignment.lead.start_date} &middot; {assignment.lead.duration}</span>
                                </div>
                                {Array.isArray(assignment.lead.items) && assignment.lead.items.length > 0 && (
                                  <div className="text-xs text-neutral-600">
                                    {assignment.lead.items.map((item, i) => (
                                      <div key={`${assignment.id}-list-item-${i}`} className="truncate">
                                        {item.product_name}
                                        {" - "}
                                        {item.spec_type === "area" && item.area_value != null
                                          ? `${item.area_value} ${item.area_unit === "sqm" ? "m²" : "sq ft"}`
                                          : `x${item.quantity ?? "0"}`}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end sm:gap-2">
                            <Select
                              value={assignment.status}
                              onValueChange={(v) => updateStage(assignment.id, v)}
                            >
                              <SelectTrigger
                                data-testid={`lead-stage-select-${assignment.id}`}
                                className="col-span-2 h-8 w-full rounded-sm border-neutral-300 text-xs sm:w-[148px]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {stageChoicesForAssignment(pipelineStages, assignment.status).map((s) => (
                                  <SelectItem key={s.key} value={s.key}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button data-testid={`view-lead-${assignment.id}`} variant="outline" size="sm"
                              onClick={() => { setSelectedLead(assignment); setNotes(assignment.notes || ""); }}
                              className="w-full rounded-sm border-neutral-300 text-xs justify-center">
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            {(assignment.quotation_count || 0) > 0 && (
                              <Button
                                data-testid={`view-quotations-${assignment.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => openQuotationsModal(assignment)}
                                className="w-full rounded-sm border-neutral-300 text-xs justify-center"
                              >
                                <FileText size={14} className="mr-1" /> Quotations ({assignment.quotation_count})
                              </Button>
                            )}
                            {assignment.status === "new" && (
                              <Button
                                data-testid={`build-quotation-${assignment.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => goToQuotationBuilder(assignment)}
                                className="w-full rounded-sm border-violet-300 text-violet-700 text-xs justify-center"
                              >
                                <FileText size={14} className="mr-1" /> Generate Quotation
                              </Button>
                            )}
                            {assignment?.lead?.source === "supplier_portal" && (
                              <Button
                                data-testid={`delete-custom-lead-${assignment.id}`}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => deleteCustomLead(assignment)}
                                disabled={deletingLeadId === assignment.lead.id}
                                className="w-full rounded-sm border-red-300 text-red-700 text-xs hover:bg-red-50 justify-center"
                              >
                                <Trash size={14} className="mr-1" />
                                {deletingLeadId === assignment.lead.id ? "Deleting..." : "Delete"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="catalog" className="mt-0 space-y-6" data-testid="supplier-catalog-tab">
            <div>
              <h2 className="text-2xl font-bold tracking-tight mb-1">Request a catalog product</h2>
              <p className="text-sm text-neutral-500 mb-4">
                Submit a product you supply. When an admin approves it, it appears on the main marketplace for buyers.
              </p>
              <Card className="rounded-sm border-neutral-200 max-w-xl">
                <CardContent className="p-4 space-y-3">
                  <form onSubmit={submitProductRequest} className="space-y-3">
                    <div>
                      <Label htmlFor="pr-name" className="text-xs font-mono uppercase text-neutral-500">Product name</Label>
                      <Input id="pr-name" data-testid="supplier-pr-name" className="rounded-sm border-neutral-300 mt-1"
                        value={requestForm.name} onChange={e => setRequestForm({ ...requestForm, name: e.target.value })}
                        placeholder="e.g. MS Square Pipe 2&quot;" required />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase text-neutral-500">Category</Label>
                      <Select
                        value={categoryNames.includes(requestForm.category) ? requestForm.category : (categoryNames[0] || "General")}
                        onValueChange={v => setRequestForm({ ...requestForm, category: v })}
                      >
                        <SelectTrigger data-testid="supplier-pr-category" className="rounded-sm border-neutral-300 mt-1">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(categoryNames.length ? categoryNames : ["General"]).map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="pr-desc" className="text-xs font-mono uppercase text-neutral-500">Description (optional)</Label>
                      <Textarea id="pr-desc" data-testid="supplier-pr-desc" className="rounded-sm border-neutral-300 mt-1 min-h-[72px]"
                        value={requestForm.description} onChange={e => setRequestForm({ ...requestForm, description: e.target.value })}
                        placeholder="Specs, sizes, or notes for the admin" />
                    </div>
                    <div>
                      <Label htmlFor="pr-img-file" className="text-xs font-mono uppercase text-neutral-500">Product image (optional)</Label>
                      <Input
                        id="pr-img-file"
                        data-testid="supplier-pr-image-file"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="rounded-sm border-neutral-300 mt-1 cursor-pointer"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (!f) return;
                          if (f.size > PRODUCT_IMAGE_MAX_BYTES) {
                            toast.error(`Image must be at most ${PRODUCT_IMAGE_MAX_KB} KB`);
                            return;
                          }
                          setRequestImageFile(f);
                          setRequestForm((prev) => ({ ...prev, image_url: "" }));
                        }}
                      />
                      <p className="text-[11px] text-neutral-500 mt-1">JPEG, PNG, WebP, or GIF — max {PRODUCT_IMAGE_MAX_KB} KB.</p>
                      {requestImageFile && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="w-14 h-14 rounded-sm overflow-hidden bg-neutral-100 border border-neutral-200 shrink-0">
                            <img src={requestImagePreview} alt="" className="w-full h-full object-cover" />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-sm text-xs h-7"
                            onClick={() => setRequestImageFile(null)}
                          >
                            Remove file
                          </Button>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="pr-img" className="text-xs font-mono uppercase text-neutral-500">Or image URL</Label>
                      <Input
                        id="pr-img"
                        data-testid="supplier-pr-image"
                        className="rounded-sm border-neutral-300 mt-1"
                        value={requestForm.image_url}
                        disabled={!!requestImageFile}
                        onChange={e => setRequestForm({ ...requestForm, image_url: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                    <Button type="submit" data-testid="supplier-pr-submit" disabled={submittingRequest}
                      className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white">
                      {submittingRequest ? "Submitting…" : "Submit request"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
            <div>
              <h3 className="font-bold mb-3">Your requests</h3>
              {productRequests.length === 0 ? (
                <p className="text-sm text-neutral-500">No requests yet.</p>
              ) : (
                <div className="space-y-2">
                  {productRequests.map((r) => (
                    <Card key={r.id} className="rounded-sm border-neutral-200" data-testid={`supplier-pr-row-${r.id}`}>
                      <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{r.name}</p>
                          <p className="text-xs text-neutral-500">{r.category}</p>
                          {r.status === "rejected" && r.review_note && (
                            <p className="text-xs text-red-600 mt-1">Note: {r.review_note}</p>
                          )}
                          {r.status === "approved" && r.product_id && (
                            <p className="text-xs text-green-700 mt-1">Live in catalog</p>
                          )}
                        </div>
                        <Badge className={`rounded-sm text-xs border shrink-0 ${
                          r.status === "pending" ? "bg-amber-100 text-amber-900 border-amber-300" :
                          r.status === "approved" ? "bg-green-100 text-green-900 border-green-300" :
                          "bg-red-100 text-red-900 border-red-300"
                        }`}>
                          {r.status}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-0 space-y-6" data-testid="supplier-profile-tab">
            <div>
              <h2 className="text-2xl font-bold tracking-tight mb-1">Your profile</h2>
              <p className="text-sm text-neutral-500 mb-4">
                Update how you appear to admins and buyers. Phone is used for WhatsApp lead alerts when the platform sends them.
              </p>
              {!profileLoaded ? (
                <p className="text-sm text-neutral-500">Loading…</p>
              ) : (
                <div className="space-y-6 max-w-2xl">
                  <Tabs value={profileSubTab} onValueChange={setProfileSubTab}>
                    <TabsList className="bg-white border border-neutral-200 rounded-sm h-10 mb-4 p-1 w-full justify-start flex-wrap">
                      <TabsTrigger value="personal" className="rounded-sm text-xs font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">Personal Profile</TabsTrigger>
                      <TabsTrigger value="products" className="rounded-sm text-xs font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">Products</TabsTrigger>
                      <TabsTrigger value="settings" className="rounded-sm text-xs font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">Settings</TabsTrigger>
                      <TabsTrigger value="cities" className="rounded-sm text-xs font-bold data-[state=active]:bg-neutral-900 data-[state=active]:text-white">Cities</TabsTrigger>
                    </TabsList>

                    <TabsContent value="personal" className="mt-0">
                      <Card className="rounded-sm border-neutral-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <User size={18} className="text-orange-600" />
                            Account &amp; contact
                          </CardTitle>
                          <p className="text-xs text-neutral-500 font-normal mt-1">
                            Sign-in email cannot be changed here; contact support if you need a new email.
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <Label htmlFor="sup-profile-name" className="text-xs font-mono uppercase text-neutral-500">Your name</Label>
                            <Input id="sup-profile-name" data-testid="supplier-profile-name" className="rounded-sm border-neutral-300 mt-1" value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Contact person or display name" autoComplete="name" />
                          </div>
                          <div>
                            <Label htmlFor="sup-profile-company" className="text-xs font-mono uppercase text-neutral-500">Company name</Label>
                            <Input id="sup-profile-company" data-testid="supplier-profile-company" className="rounded-sm border-neutral-300 mt-1" value={profileCompany} onChange={(e) => setProfileCompany(e.target.value)} placeholder="Optional" autoComplete="organization" />
                          </div>
                          <div>
                            <Label htmlFor="sup-profile-email" className="text-xs font-mono uppercase text-neutral-500">Email</Label>
                            <Input id="sup-profile-email" data-testid="supplier-profile-email" className="rounded-sm border-neutral-300 mt-1 bg-neutral-50 text-neutral-600" value={profileEmail} readOnly aria-readonly="true" />
                          </div>
                          <div>
                            <Label htmlFor="sup-profile-phone" className="text-xs font-mono uppercase text-neutral-500">Phone / WhatsApp</Label>
                            <Input id="sup-profile-phone" data-testid="supplier-profile-phone" type="tel" className="rounded-sm border-neutral-300 mt-1" value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)} placeholder="e.g. +971501234567 or 0501234567" autoComplete="tel" />
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="products" className="mt-0">
                      <Card className="rounded-sm border-neutral-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Products</CardTitle>
                          <p className="text-xs text-neutral-500 font-normal mt-1">Set quantity, rate/day and weight. These values prefill the quotation table automatically.</p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {profileProductLines.length > 0 && (
                            <div className="space-y-2 border border-neutral-200 rounded-sm p-3 bg-neutral-50">
                              <p className="text-xs font-mono uppercase text-neutral-500">Product defaults</p>
                              {profileProductLines.map((l) => {
                                const p = catalogForProfile.find((c) => c.id === l.product_id);
                                return (
                                  <div key={l.product_id} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
                                    <div className="flex items-center gap-2 sm:col-span-1">
                                      <span className="text-xs min-w-0 truncate font-medium">{p?.name || l.product_id}</span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs text-neutral-500"
                                        onClick={() => removeProfileProduct(l.product_id)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                    <Input type="text" inputMode="numeric" autoComplete="off" placeholder="Qty" className="h-8 rounded-sm border-neutral-300 text-xs" data-testid={`profile-product-qty-${l.product_id}`} value={l.quantity} onChange={(e) => setProfileLineQty(l.product_id, e.target.value)} />
                                    <Input type="text" inputMode="decimal" autoComplete="off" placeholder="Rate/Day" className="h-8 rounded-sm border-neutral-300 text-xs" data-testid={`profile-product-rate-${l.product_id}`} value={l.rate_per_day} onChange={(e) => setProfileLineRate(l.product_id, e.target.value)} />
                                    <Input type="text" inputMode="decimal" autoComplete="off" placeholder="Weight" className="h-8 rounded-sm border-neutral-300 text-xs" data-testid={`profile-product-weight-${l.product_id}`} value={l.weight} onChange={(e) => setProfileLineWeight(l.product_id, e.target.value)} />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {profileProductLines.length === 0 && (
                            <div className="text-xs text-neutral-500 border border-dashed border-neutral-300 rounded-sm p-3">
                              No products selected yet.
                            </div>
                          )}
                          <div className="border border-neutral-200 rounded-sm p-3">
                            <p className="text-xs font-mono uppercase text-neutral-500 mb-2">Add product</p>
                            {catalogForProfile.filter((p) => !profileProductLines.some((l) => l.product_id === p.id)).length === 0 ? (
                              <p className="text-xs text-neutral-500">No products available.</p>
                            ) : (
                              <div className="flex flex-col sm:flex-row gap-2">
                                <Select value={productToAdd} onValueChange={setProductToAdd}>
                                  <SelectTrigger className="rounded-sm border-neutral-300">
                                    <SelectValue placeholder="Select product" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {catalogForProfile
                                      .filter((p) => !profileProductLines.some((l) => l.product_id === p.id))
                                      .map((p) => (
                                        <SelectItem key={p.id} value={p.id}>
                                          {p.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                                <Button type="button" onClick={addProfileProduct} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white">
                                  Add Product
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="settings" className="mt-0">
                      <Card className="rounded-sm border-neutral-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Gear size={18} className="text-neutral-500" />
                            Settings
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <Label className="text-xs font-mono uppercase text-neutral-500">Supplier type</Label>
                            <Select value={profileSupplierType} onValueChange={setProfileSupplierType}>
                              <SelectTrigger data-testid="supplier-profile-type" className="rounded-sm border-neutral-300 mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="rental">Rental</SelectItem>
                                <SelectItem value="sale">Sale</SelectItem>
                                <SelectItem value="hybrid">Hybrid</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs font-mono uppercase text-neutral-500 flex items-center gap-1.5">
                              <ImageIcon size={14} className="text-orange-600" aria-hidden />
                              Company logo
                            </Label>
                            <p className="text-[11px] text-neutral-500 mt-1 mb-2">
                              Shown on the public supplier directory. Max {PRODUCT_IMAGE_MAX_KB} KB (JPEG, PNG, WebP, GIF).
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="w-24 h-24 rounded-sm border-2 border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
                                {profileLogoUrl ? (
                                  <img src={publicAssetUrl(profileLogoUrl)} alt="" className="max-w-full max-h-full object-contain" />
                                ) : (
                                  <ImageIcon size={36} className="text-neutral-300" weight="duotone" />
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <input ref={logoFileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="supplier-profile-logo-file" onChange={onLogoFileSelected} />
                                <Button type="button" variant="outline" size="sm" className="rounded-sm border-neutral-300" disabled={logoUploading} data-testid="supplier-profile-logo-upload" onClick={() => logoFileInputRef.current?.click()}>
                                  {logoUploading ? "Working…" : "Upload image"}
                                </Button>
                                {profileLogoUrl ? (
                                  <Button type="button" variant="ghost" size="sm" className="rounded-sm text-neutral-600" disabled={logoUploading} data-testid="supplier-profile-logo-remove" onClick={removeProfileLogo}>
                                    Remove
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="pt-2 border-t border-neutral-200">
                            <p className="text-sm font-semibold mb-2">Quotation settings</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-mono uppercase text-neutral-500">Supplier name</Label>
                                <Input
                                  className="rounded-sm border-neutral-300 mt-1"
                                  value={quotationSupplierName}
                                  onChange={(e) => setQuotationSupplierName(e.target.value)}
                                  placeholder="Printed on quotation"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-mono uppercase text-neutral-500">Supplier GSTIN</Label>
                                <Input
                                  className="rounded-sm border-neutral-300 mt-1"
                                  value={quotationSupplierGstin}
                                  onChange={(e) => setQuotationSupplierGstin(e.target.value)}
                                  placeholder="GSTIN"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-xs font-mono uppercase text-neutral-500">Supplier address</Label>
                                <Textarea
                                  className="rounded-sm border-neutral-300 mt-1 min-h-[70px]"
                                  value={quotationSupplierAddress}
                                  onChange={(e) => setQuotationSupplierAddress(e.target.value)}
                                  placeholder="Address shown in quotation"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-mono uppercase text-neutral-500">Default security amount</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  className="rounded-sm border-neutral-300 mt-1"
                                  value={quotationSecurityAmount}
                                  onChange={(e) => setQuotationSecurityAmount(String(e.target.value).replace(/[^0-9.]/g, ""))}
                                  placeholder="e.g. 50000"
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-mono uppercase text-neutral-500">Quotation watermark image</Label>
                                <div className="flex items-center gap-2 mt-1">
                                  <input
                                    ref={quotationWatermarkInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                    onChange={onQuotationWatermarkSelected}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="rounded-sm border-neutral-300"
                                    disabled={quotationWatermarkUploading}
                                    onClick={() => quotationWatermarkInputRef.current?.click()}
                                  >
                                    {quotationWatermarkUploading ? "Uploading…" : "Upload watermark"}
                                  </Button>
                                  {quotationWatermarkUrl ? (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-sm text-neutral-600"
                                      onClick={() => setQuotationWatermarkUrl("")}
                                    >
                                      Remove
                                    </Button>
                                  ) : null}
                                </div>
                                {quotationWatermarkUrl ? (
                                  <img
                                    src={publicAssetUrl(quotationWatermarkUrl)}
                                    alt=""
                                    className="mt-2 h-16 w-auto rounded-sm border border-neutral-200 object-contain"
                                  />
                                ) : null}
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-xs font-mono uppercase text-neutral-500">Default terms &amp; conditions</Label>
                                <Textarea
                                  className="rounded-sm border-neutral-300 mt-1 min-h-[130px]"
                                  value={quotationTerms}
                                  onChange={(e) => setQuotationTerms(e.target.value)}
                                  placeholder="These lines will prefill in quotation builder"
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="cities" className="mt-0">
                      <Card className="overflow-visible rounded-sm border-neutral-200">
                        <CardHeader className="border-b border-neutral-100 bg-gradient-to-r from-orange-50/70 to-white pb-4">
                          <div className="flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-orange-100 text-orange-700">
                              <MapPin size={18} weight="duotone" />
                            </div>
                            <div className="min-w-0">
                              <CardTitle className="text-base">Service cities</CardTitle>
                              <p className="mt-0.5 text-xs leading-relaxed text-neutral-500">
                                Add every city where you supply materials. Buyers in those areas can find and contact you.
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-5 pt-5">
                          <div className="relative z-10">
                            <Label htmlFor="profile-city-search-field" className="text-sm font-medium text-neutral-800">
                              Add a city
                            </Label>
                            <p className="mt-0.5 text-xs text-neutral-500">
                              Start typing — suggestions open above the field when space is tight.
                            </p>
                            <div className="mt-2">
                              <CityGoogleAutocomplete
                                value={profileCitySearch}
                                onChange={setProfileCitySearch}
                                onSelect={addProfileCityFromSearch}
                                placeholder="Search cities to add…"
                                className="profile-city-autocomplete"
                                containerTestId="profile-city-autocomplete"
                                inputTestId="profile-city-search"
                                predictionsPlacement="auto"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                              Your cities
                              {profileCities.length > 0 ? (
                                <span className="ml-1.5 font-mono text-orange-600">{profileCities.length}</span>
                              ) : null}
                            </Label>
                            {profileCities.length > 0 ? (
                              <div className="mt-2.5 flex flex-wrap gap-2">
                                {profileCities.map((city) => (
                                  <button
                                    key={`selected-${city}`}
                                    type="button"
                                    data-testid={`profile-city-selected-${city}`}
                                    onClick={() => toggleProfileCity(city)}
                                    className="group inline-flex max-w-[11rem] min-w-0 items-center gap-1.5 rounded-sm border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-left text-xs font-medium text-orange-900 transition-colors hover:border-orange-300 hover:bg-orange-100"
                                    title={city}
                                  >
                                    <MapPin size={12} className="shrink-0 text-orange-500" weight="fill" />
                                    <span className="min-w-0 flex-1 truncate">{city}</span>
                                    <X
                                      size={12}
                                      className="shrink-0 text-orange-400 transition-colors group-hover:text-orange-700"
                                    />
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-2.5 rounded-sm border border-dashed border-neutral-200 bg-neutral-50/60 px-4 py-8 text-center">
                                <MapPin size={28} className="mx-auto mb-2 text-neutral-300" weight="duotone" />
                                <p className="text-sm font-medium text-neutral-700">No cities added yet</p>
                                <p className="mt-1 text-xs text-neutral-400">Search above and pick a city to add it</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                  <Button
                    data-testid="supplier-save-profile"
                    onClick={saveProfile}
                    disabled={savingProfile}
                    className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {savingProfile ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showPipelineSetup} onOpenChange={setShowPipelineSetup}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm"
          data-testid="supplier-setup-pipeline-dialog"
        >
          <DialogHeader>
            <DialogTitle>Setup Pipeline</DialogTitle>
            <DialogDescription>
              Default stages are fixed. Add custom stages for your workflow — you can delete custom stages only when no leads use them.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-mono uppercase text-neutral-500 mb-2">Default stages (fixed)</p>
              <ul className="space-y-2">
                {FIXED_PIPELINE_STAGES.map((s) => (
                  <li
                    key={s.key}
                    className="flex items-center justify-between gap-2 rounded-sm border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <Badge className={`rounded-sm text-xs border ${s.color}`}>{s.label}</Badge>
                    </span>
                    <span className="text-[10px] font-mono uppercase text-neutral-400">Locked</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-mono uppercase text-neutral-500 mb-2">Custom stages</p>
              {customPipelineStages.length === 0 ? (
                <p className="text-sm text-neutral-500 py-2">No custom stages yet.</p>
              ) : (
                <ul className="space-y-2 mb-3">
                  {customPipelineStages.map((s) => (
                    <li
                      key={s.key}
                      className="flex items-center justify-between gap-2 rounded-sm border border-neutral-200 px-3 py-2"
                    >
                      <Badge className={`rounded-sm text-xs border ${s.color || "bg-neutral-100 text-neutral-800 border-neutral-300"}`}>
                        {s.label}
                      </Badge>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={pipelineSaving}
                        onClick={() => deleteCustomPipelineStage(s.key)}
                        className="rounded-sm border-red-300 text-red-700 text-xs hover:bg-red-50"
                        data-testid={`delete-pipeline-stage-${s.key}`}
                      >
                        <Trash size={14} className="mr-1" /> Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={addCustomPipelineStage} className="flex gap-2">
                <Input
                  value={newPipelineStageLabel}
                  onChange={(e) => setNewPipelineStageLabel(e.target.value)}
                  placeholder="e.g. Follow-up, Negotiation"
                  className="rounded-sm border-neutral-300"
                  maxLength={40}
                  data-testid="new-pipeline-stage-input"
                />
                <Button
                  type="submit"
                  disabled={pipelineSaving || !newPipelineStageLabel.trim()}
                  className="rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white shrink-0"
                  data-testid="add-pipeline-stage-btn"
                >
                  <Plus size={16} className="mr-1" /> Add
                </Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddLeadDialog} onOpenChange={setShowAddLeadDialog}>
        <DialogContent
          className="max-w-2xl max-h-[92vh] flex flex-col overflow-hidden gap-4 rounded-sm p-6"
          data-testid="supplier-add-lead-dialog"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={submitSupplierLead}
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden"
          >
            <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Buyer Name</Label>
                <Input
                  className={addLeadFieldClassName}
                  value={newLeadForm.buyer_name}
                  onChange={(e) => setNewLeadForm((prev) => ({ ...prev, buyer_name: e.target.value }))}
                  placeholder="Contact person"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Buyer Company</Label>
                <Input
                  className={addLeadFieldClassName}
                  value={newLeadForm.buyer_company}
                  onChange={(e) => setNewLeadForm((prev) => ({ ...prev, buyer_company: e.target.value }))}
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Buyer Phone</Label>
                <Input
                  className={addLeadFieldClassName}
                  value={newLeadForm.buyer_phone}
                  onChange={(e) => setNewLeadForm((prev) => ({ ...prev, buyer_phone: e.target.value }))}
                  placeholder="Phone number"
                  required
                />
              </div>
              <div className="relative z-20">
                <Label className="text-xs font-mono uppercase text-neutral-500">City</Label>
                <CityGoogleAutocomplete
                  value={newLeadForm.city}
                  onChange={(v) => setNewLeadForm((prev) => ({ ...prev, city: v }))}
                  onSelect={(v) => setNewLeadForm((prev) => ({ ...prev, city: v }))}
                  placeholder="Search city…"
                  predictionsPlacement="auto"
                  className={addLeadCityClassName}
                  containerTestId="add-lead-city"
                  inputTestId="add-lead-city-search"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Start Date</Label>
                <Input
                  className={addLeadFieldClassName}
                  type="date"
                  value={newLeadForm.start_date}
                  onChange={(e) => setNewLeadForm((prev) => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase text-neutral-500">Requirement Type</Label>
                <Select
                  value={newLeadForm.requirement_type}
                  onValueChange={(v) =>
                    setNewLeadForm((prev) => ({
                      ...prev,
                      requirement_type: v === "buy" ? "buy" : "rent",
                      duration: v === "buy" ? "" : prev.duration,
                    }))
                  }
                >
                  <SelectTrigger className={addLeadFieldClassName}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newLeadForm.requirement_type === "rent" && (
                <div>
                  <Label className="text-xs font-mono uppercase text-neutral-500">Duration</Label>
                  <Input
                    className={addLeadFieldClassName}
                    value={newLeadForm.duration}
                    onChange={(e) => setNewLeadForm((prev) => ({ ...prev, duration: e.target.value }))}
                    placeholder="e.g. 15 days"
                    required
                  />
                </div>
              )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                <Label className="text-xs font-mono uppercase text-neutral-500">Products</Label>
                {leadFormCatalogLoading ? (
                  <span className="text-[11px] text-neutral-500">Loading your catalog…</span>
                ) : leadFormCatalogProducts.length === 0 ? (
                  <span className="text-[11px] text-neutral-500">
                    Add products under Profile to pick them here, or use custom product.
                  </span>
                ) : null}
              </div>
              <div
                className="min-h-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden overscroll-contain pr-1"
                data-testid="add-lead-products-scroll"
              >
              {newLeadForm.items.map((it, idx) => {
                const pick =
                  it.line_pick ||
                  (it.product_id ? it.product_id : ADD_LEAD_CUSTOM_PRODUCT);
                const catalogP =
                  pick !== ADD_LEAD_CUSTOM_PRODUCT
                    ? leadFormCatalogProducts.find((x) => x.id === pick)
                    : null;
                const rawImg = catalogP?.image_url ? String(catalogP.image_url).trim() : "";
                const thumbSrc = rawImg
                  ? rawImg.startsWith("http")
                    ? rawImg
                    : publicAssetUrl(rawImg)
                  : null;
                const qtyNum = Math.max(1, parseInt(String(it.quantity || "1"), 10) || 1);
                const areaNum = parseFloat(String(it.area_value || "").replace(/,/g, "")) || 0;
                const sizeOpts = catalogP ? parseProductSizes(catalogP.sizes) : [];
                const sizeDisplay = it.size
                  ? formatLineSizeDisplay(it.size, it.product_name || catalogP?.name)
                  : "";
                return (
                  <div
                    key={`new-lead-item-${idx}`}
                    className="rounded-sm border border-orange-200/90 bg-amber-50/50 p-2 sm:p-2.5"
                  >
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <div className="w-11 h-11 shrink-0 rounded-sm border border-orange-200/80 bg-white overflow-hidden flex items-center justify-center">
                          {thumbSrc ? (
                            <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package size={22} className="text-orange-300" aria-hidden />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <Select
                            value={pick}
                            onValueChange={(v) => onAddLeadProductPick(idx, v)}
                            disabled={leadFormCatalogLoading}
                          >
                            <SelectTrigger
                              className={addLeadNestedFieldClassName}
                              data-testid={`add-lead-product-pick-${idx}`}
                            >
                              <SelectValue placeholder="Select product" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={ADD_LEAD_CUSTOM_PRODUCT}>Custom product (type name)</SelectItem>
                              {leadFormCatalogProducts.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {pick === ADD_LEAD_CUSTOM_PRODUCT ? (
                            <Input
                              className={addLeadNestedFieldClassName}
                              value={it.product_name}
                              onChange={(e) => updateNewLeadItem(idx, "product_name", e.target.value)}
                              placeholder="Product name"
                              required
                            />
                          ) : (
                            <p className="text-[11px] text-neutral-500 leading-tight">
                              <span className="font-medium text-neutral-700">{it.product_name || "—"}</span>
                              {catalogP?.category ? (
                                <span className="text-neutral-400"> · {catalogP.category}</span>
                              ) : null}
                            </p>
                          )}
                        </div>
                      </div>

                      {pick !== ADD_LEAD_CUSTOM_PRODUCT && catalogP ? (
                        <div className="flex flex-col gap-1 shrink-0">
                          <span className="text-[10px] font-mono uppercase text-neutral-500">Size</span>
                          {sizeOpts.length > 0 ? (
                            sizeDisplay ? (
                              <span
                                className="inline-flex h-9 min-w-[3rem] max-w-[7rem] items-center justify-center rounded-sm border border-orange-200 bg-orange-50 px-2.5 text-xs font-bold text-orange-700"
                                title={sizeDisplay}
                                data-testid={`add-lead-size-${idx}`}
                              >
                                <span className="truncate">{sizeDisplay}</span>
                              </span>
                            ) : (
                              <Select
                                value="__none__"
                                onValueChange={(v) =>
                                  updateNewLeadItem(idx, "size", v === "__none__" ? "" : v)
                                }
                              >
                                <SelectTrigger
                                  className="h-9 w-[5.5rem] sm:w-[92px] rounded-sm border-neutral-300 text-xs"
                                  data-testid={`add-lead-size-${idx}`}
                                >
                                  <SelectValue placeholder="Size" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__" className="text-neutral-500">
                                    Select
                                  </SelectItem>
                                  {sizeOpts.map((s) => (
                                    <SelectItem key={s} value={s}>
                                      {formatLineSizeDisplay(s, catalogP?.name)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )
                          ) : (
                            <span className="flex h-9 items-center text-xs text-neutral-400">—</span>
                          )}
                        </div>
                      ) : null}

                      <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:shrink-0">
                        <div className="inline-flex rounded-sm border border-orange-300 overflow-hidden">
                          <Button
                            type="button"
                            variant={it.spec_type !== "area" ? "default" : "ghost"}
                            size="sm"
                            className={`rounded-none h-9 px-3 text-xs ${
                              it.spec_type !== "area"
                                ? "bg-orange-600 hover:bg-orange-700 text-white"
                                : "text-neutral-600"
                            }`}
                            onClick={() => onAddLeadSpecTypeChange(idx, "quantity")}
                            data-testid={`add-lead-spec-qty-${idx}`}
                          >
                            Quantity
                          </Button>
                          <Button
                            type="button"
                            variant={it.spec_type === "area" ? "default" : "ghost"}
                            size="sm"
                            className={`rounded-none h-9 px-3 text-xs border-l border-orange-300 ${
                              it.spec_type === "area"
                                ? "bg-orange-600 hover:bg-orange-700 text-white"
                                : "text-neutral-600"
                            }`}
                            onClick={() => onAddLeadSpecTypeChange(idx, "area")}
                            data-testid={`add-lead-spec-area-${idx}`}
                          >
                            Area
                          </Button>
                        </div>

                        {it.spec_type === "area" ? (
                          <div className="flex items-center gap-1 flex-wrap">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 rounded-sm border-neutral-300 shrink-0"
                              onClick={() => {
                                const next = Math.max(0, Math.round((areaNum - 1) * 100) / 100);
                                updateNewLeadItem(idx, "area_value", next > 0 ? String(next) : "");
                              }}
                              aria-label="Decrease area"
                            >
                              <Minus size={14} />
                            </Button>
                            <Input
                              className="h-9 w-[4.5rem] text-center rounded-sm border-neutral-300 px-1"
                              inputMode="decimal"
                              value={it.area_value}
                              onChange={(e) =>
                                updateNewLeadItem(
                                  idx,
                                  "area_value",
                                  String(e.target.value).replace(/[^0-9.]/g, "")
                                )
                              }
                              placeholder="0"
                              aria-label="Area value"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 rounded-sm border-neutral-300 shrink-0"
                              onClick={() => {
                                const next = Math.round((areaNum + 1) * 100) / 100;
                                updateNewLeadItem(idx, "area_value", String(next));
                              }}
                              aria-label="Increase area"
                            >
                              <Plus size={14} />
                            </Button>
                            <Select
                              value={it.area_unit === "sqm" ? "sqm" : "sqft"}
                              onValueChange={(v) =>
                                updateNewLeadItem(idx, "area_unit", v === "sqm" ? "sqm" : "sqft")
                              }
                            >
                              <SelectTrigger
                                className="rounded-sm border-neutral-300 h-9 w-[5.5rem] text-xs"
                                data-testid={`add-lead-spec-${idx}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sqft">sq ft</SelectItem>
                                <SelectItem value="sqm">m²</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 rounded-sm border-neutral-300"
                              onClick={() =>
                                updateNewLeadItem(idx, "quantity", String(Math.max(1, qtyNum - 1)))
                              }
                              aria-label="Decrease quantity"
                            >
                              <Minus size={14} />
                            </Button>
                            <Input
                              className="h-9 w-12 text-center rounded-sm border-neutral-300 px-1"
                              inputMode="numeric"
                              value={it.quantity}
                              onChange={(e) =>
                                updateNewLeadItem(idx, "quantity", String(e.target.value).replace(/\D/g, ""))
                              }
                              aria-label="Quantity"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-9 w-9 rounded-sm border-neutral-300"
                              onClick={() => updateNewLeadItem(idx, "quantity", String(qtyNum + 1))}
                              aria-label="Increase quantity"
                            >
                              <Plus size={14} />
                            </Button>
                          </div>
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                          onClick={() => removeNewLeadItem(idx)}
                          data-testid={`add-lead-remove-item-${idx}`}
                          aria-label="Remove line"
                        >
                          <X size={18} weight="bold" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addNewLeadItem}
                className="shrink-0"
              >
                <PlusCircle size={14} className="mr-1" /> Add Product
              </Button>
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-neutral-100 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddLeadDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creatingLead} className="bg-orange-600 hover:bg-orange-700 text-white">
                {creatingLead ? "Creating..." : "Create Lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => !open && setSelectedLead(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[85vh] overflow-hidden flex flex-col" data-testid="lead-detail-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              Lead Details
              <span className="text-xs font-mono text-neutral-400">{selectedLead?.id?.slice(0, 8)}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedLead?.lead && (
            <div className="space-y-4 min-h-0 overflow-y-auto pr-1">
              <div className="border border-neutral-200 rounded-sm overflow-hidden">
                <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-xs font-bold uppercase tracking-wider text-neutral-500 font-mono">
                  Contact Information
                </div>
                <div className="divide-y divide-neutral-100">
                  <div className="px-3 py-2.5 flex items-start gap-3 text-sm">
                    <User size={16} className="text-neutral-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase text-neutral-500">Buyer name</p>
                      <p className="font-medium text-neutral-900 break-words">{leadDetailValue(selectedLead.lead.buyer_name)}</p>
                    </div>
                  </div>
                  <div className="px-3 py-2.5 flex items-start gap-3 text-sm">
                    <Buildings size={16} className="text-neutral-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase text-neutral-500">Company</p>
                      <p className="font-medium text-neutral-900 break-words">{leadDetailValue(selectedLead.lead.buyer_company)}</p>
                    </div>
                  </div>
                  <div className="px-3 py-2.5 flex items-start gap-3 text-sm">
                    <Phone size={16} className="text-neutral-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase text-neutral-500">Phone</p>
                      {selectedLead.lead.buyer_phone ? (
                        <a
                          href={`tel:${selectedLead.lead.buyer_phone}`}
                          className="font-medium text-orange-700 hover:underline break-all"
                        >
                          {selectedLead.lead.buyer_phone}
                        </a>
                      ) : (
                        <p className="font-medium text-neutral-900">—</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-neutral-50 rounded-sm border border-neutral-200">
                  <p className="text-xs text-neutral-500 font-mono uppercase">City</p>
                  <p className="font-bold text-sm">{selectedLead.lead.city}</p>
                </div>
                <div className="p-3 bg-neutral-50 rounded-sm border border-neutral-200">
                  <p className="text-xs text-neutral-500 font-mono uppercase">Type</p>
                  <p className="font-bold text-sm">{leadRequirementLabel(selectedLead.lead.requirement_type)}</p>
                </div>
                <div className="p-3 bg-neutral-50 rounded-sm border border-neutral-200">
                  <p className="text-xs text-neutral-500 font-mono uppercase">Schedule</p>
                  <p className="font-bold text-sm">{selectedLead.lead.start_date} &middot; {selectedLead.lead.duration}</p>
                </div>
              </div>

              <div className="border border-neutral-200 rounded-sm overflow-hidden">
                <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-xs font-bold uppercase tracking-wider text-neutral-500 font-mono">
                  Items Required
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {selectedLead.lead.items?.length ? (
                    selectedLead.lead.items.map((item, i) => (
                      <div key={i} className="px-3 py-2 flex justify-between gap-2 border-b border-neutral-100 last:border-0 text-sm">
                        <span className="min-w-0">
                          {item.product_name}
                          {item.size ? <span className="text-neutral-500"> ({item.size})</span> : null}
                        </span>
                        <span className="font-mono shrink-0 text-right">
                          {item.spec_type === "area" && item.area_value != null
                            ? `${item.area_value} ${item.area_unit === "sqm" ? "m²" : "sq ft"}`
                            : `×${item.quantity ?? "—"}`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-neutral-400">No items listed</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs text-neutral-500 font-mono uppercase mb-1">Response Time</p>
                <div className="flex gap-3">
                  {selectedLead.opened_at && (
                    <Badge className="rounded-sm text-xs bg-blue-50 text-blue-700 border border-blue-200">
                      <Clock size={10} className="mr-1" /> Opened
                    </Badge>
                  )}
                  {selectedLead.contacted_at && (
                    <Badge className="rounded-sm text-xs bg-amber-50 text-amber-700 border border-amber-200">
                      <Clock size={10} className="mr-1" /> Contacted
                    </Badge>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-neutral-500 font-mono uppercase mb-1 block">Stage</label>
                <Select value={selectedLead.status} onValueChange={(v) => updateStage(selectedLead.id, v)}>
                  <SelectTrigger data-testid="stage-select" className="rounded-sm border-neutral-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stageChoicesForAssignment(pipelineStages, selectedLead.status).map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(selectedLead.quotation_count || 0) > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openQuotationsModal(selectedLead)}
                      className="rounded-sm border-neutral-300 text-xs"
                    >
                      <FileText size={14} className="mr-1" /> View quotations ({selectedLead.quotation_count})
                    </Button>
                  )}
                  {selectedLead.status === "new" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => goToQuotationBuilder(selectedLead)}
                      className="rounded-sm border-violet-300 text-violet-700 text-xs"
                    >
                      <FileText size={14} className="mr-1" /> Generate Quotation
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-neutral-500 font-mono uppercase mb-1 block">Notes</label>
                <Textarea data-testid="lead-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this lead..." className="rounded-sm border-neutral-300 min-h-[80px]" />
                <Button data-testid="save-notes-btn" size="sm" onClick={() => saveNotes(selectedLead.id)}
                  className="mt-2 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white text-xs">
                  Save Notes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!quotationsModalAssignment}
        onOpenChange={(open) => {
          if (!open) {
            setQuotationsModalAssignment(null);
            setQuotationsList([]);
          }
        }}
      >
        <DialogContent className="max-w-md rounded-sm" data-testid="supplier-quotations-list-dialog">
          <DialogHeader>
            <DialogTitle>Saved quotations</DialogTitle>
          </DialogHeader>
          {quotationsListLoading ? (
            <p className="text-sm text-neutral-500 py-4">Loading…</p>
          ) : quotationsList.length === 0 ? (
            <p className="text-sm text-neutral-500 py-4">No saved quotations yet.</p>
          ) : (
            <ul className="space-y-2 max-h-[min(60vh,320px)] overflow-y-auto">
              {quotationsList.map((q) => (
                <li
                  key={q.id}
                  className="flex items-center justify-between gap-2 rounded-sm border border-neutral-200 bg-neutral-50/80 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-semibold">No. {q.quote_no}</p>
                    <p className="text-[11px] text-neutral-500">
                      {q.updated_at ? new Date(q.updated_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm shrink-0"
                    onClick={() => {
                      const a = quotationsModalAssignment;
                      setQuotationsModalAssignment(null);
                      setQuotationsList([]);
                      goToQuotationBuilder(a, q.id);
                    }}
                  >
                    Open
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {quotationsModalAssignment && !quotationsListLoading ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full rounded-sm border-violet-300 text-violet-700"
              onClick={() => {
                const a = quotationsModalAssignment;
                setQuotationsModalAssignment(null);
                setQuotationsList([]);
                goToQuotationBuilder(a);
              }}
            >
              <PlusCircle size={14} className="mr-1" /> New quotation (same lead)
            </Button>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
